import { useMemo } from 'react';
import { areSearchResultsTheSameMeal } from '../../services/meals';
import {
  useGetDistilledMealsByParentsQuery,
  useGetSharedBoardsByIDsQuery,
  useGetDistilledSharedMealsByParentsQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  useGetHealthProBoardsQuery,
  useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { SHARED_BOARDS_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

export const useAllMultiMenuMeals = () => {
  const {
    data: spaceAssignedSharedRecipesBoardsIDs,
    isLoading: isSaceAssignedSharedRecipesBoardsIDsLoading,
  } = useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery();

  const { data: healthProBoards, isLoading: isHealthProBoardsLoading } =
    useGetHealthProBoardsQuery();

  const { data: sharedBoards, isLoading: isSharedBoardsLoading } =
    useGetSharedBoardsByIDsQuery(spaceAssignedSharedRecipesBoardsIDs, {
      skip:
        isSaceAssignedSharedRecipesBoardsIDsLoading ||
        !spaceAssignedSharedRecipesBoardsIDs ||
        spaceAssignedSharedRecipesBoardsIDs.length === 0,
      refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
    });

  const allBoards = useMemo(
    () => [...(healthProBoards || []), ...(sharedBoards || [])],
    [healthProBoards, sharedBoards],
  );

  const healthProBoardIDs = useMemo(
    () => (healthProBoards || []).map((b) => b.id),
    [healthProBoards],
  );

  const sharedBoardIDs = useMemo(
    () => (sharedBoards || []).map((b) => b.id),
    [sharedBoards],
  );

  const flattenedSharedBoardsMenus = useMemo(
    () =>
      allBoards.flatMap((b) =>
        (b.menus || []).map((menu) => ({
          parentID: b.id,
          parentTitle: b.title,
          menuID: menu.id,
          menuTitle: menu.title,
          mealIDs: menu.mealIDs,
          sharedMealIDs: menu.sharedMealIDs,
          formattedMenuTitle: menu.title,
        })),
      ),
    [allBoards],
  );

  // Make sure that, after flattening, the meals are "unique"

  const {
    data: distilledHealthProMealsByParents,
    isLoading: isDistilledHealthProMealsByParentsLoading,
  } = useGetDistilledMealsByParentsQuery(healthProBoardIDs, {
    skip: !healthProBoardIDs || healthProBoardIDs.length === 0,
  });

  const {
    data: distilledSharedMealsByParents,
    isLoading: isDistilledSharedMealsByParentsLoading,
  } = useGetDistilledSharedMealsByParentsQuery(sharedBoardIDs, {
    skip: !sharedBoardIDs || sharedBoardIDs.length === 0,
  });

  const areDistilledMealsLoading =
    isDistilledHealthProMealsByParentsLoading ||
    isDistilledSharedMealsByParentsLoading;

  const flattenedSharedBoardsTitleMap = useMemo(() => {
    const titleMap = {};
    flattenedSharedBoardsMenus.forEach((menu) => {
      const existingEntry = titleMap[menu.menuTitle];
      let newEntry = existingEntry;
      if (!existingEntry) {
        newEntry = {
          menus: [
            {
              parentID: menu.parentID,
              parentTitle: menu.parentTitle,
              menuID: menu.menuID,
              mealIDs: menu.mealIDs,
              sharedMealIDs: menu.sharedMealIDs,
            },
          ],
          menuTitle: menu.menuTitle,
        };
      } else if (
        !(existingEntry.menus || []).some(
          (m) => m.parentID === menu.parentID && m.menuID === menu.menuID,
        )
      ) {
        newEntry = {
          ...existingEntry,
          menus: [
            ...(existingEntry.menus || []),
            {
              parentID: menu.parentID,
              parentTitle: menu.parentTitle,
              menuID: menu.menuID,
              mealIDs: menu.mealIDs,
              sharedMealIDs: menu.sharedMealIDs,
            },
          ],
        };
      }
      titleMap[menu.menuTitle] = newEntry;
    });

    if (!areDistilledMealsLoading) {
      // Can't run deduplication until we have all the distilled meals
      Object.values(titleMap).forEach((entry) => {
        const entryDistinctMeals = [];
        (entry.menus || []).forEach((menu) => {
          const distilledSharedMeals =
            (distilledHealthProMealsByParents || {})[menu.parentID] ||
            (distilledSharedMealsByParents || {})[menu.parentID] ||
            {};
          // console.log(`For entry ${entry.menuTitle}, parentID ${menu.parentID} found ${Object.keys(sharedMeals).length} meals.`);
          if (menu.mealIDs) {
            const newMealIDs = [];
            menu.mealIDs.forEach((mealID) => {
              const distilledMeal = distilledSharedMeals[mealID];
              if (!distilledMeal) {
                // console.log(`Skipping meal ID ${mealID}, meal not found.`);
                return;
              }
              if (
                !entryDistinctMeals.some((existingMeal) =>
                  areSearchResultsTheSameMeal(
                    existingMeal.recipes[0],
                    distilledMeal.recipes[0],
                  ),
                )
              ) {
                entryDistinctMeals.push(distilledMeal);
                newMealIDs.push(mealID);
              } else {
                console.log(
                  `Skipping meal ID ${mealID}, similar to an existing meal.`,
                );
              }
            });
            // eslint-disable-next-line no-param-reassign
            menu.mealIDs = newMealIDs;
            console.log(`newMealIDs = ${JSON.stringify(newMealIDs)}`);
          }

          // TODO not sure if still used
          if (menu.sharedMealIDs) {
            const newSharedMealIDs = [];
            menu.sharedMealIDs.forEach((sharedMealID) => {
              const distilledMeal = distilledSharedMeals[sharedMealID];
              if (!distilledMeal) {
                console.log(
                  `Skipping shared meal ID ${sharedMealID}, shared meal not found.`,
                );
                return;
              }
              if (
                !entryDistinctMeals.some((existingMeal) =>
                  areSearchResultsTheSameMeal(
                    existingMeal.recipes[0],
                    distilledMeal.recipes[0],
                  ),
                )
              ) {
                entryDistinctMeals.push(distilledMeal);
                newSharedMealIDs.push(sharedMealID);
              } else {
                console.log(
                  `Skipping shared meal ID ${sharedMealID}, similar to an existing meal.`,
                );
              }
            });
            // eslint-disable-next-line no-param-reassign
            menu.sharedMealIDs = newSharedMealIDs;
            // console.log(`newSharedMealIDs = ${JSON.stringify(newSharedMealIDs)}`);
          }
        });
      });
    }
    return titleMap;
  }, [
    areDistilledMealsLoading,
    distilledHealthProMealsByParents,
    distilledSharedMealsByParents,
    flattenedSharedBoardsMenus,
  ]);

  const isLoading =
    isHealthProBoardsLoading ||
    isSaceAssignedSharedRecipesBoardsIDsLoading ||
    isSharedBoardsLoading ||
    isDistilledHealthProMealsByParentsLoading ||
    isDistilledSharedMealsByParentsLoading;

  const memoizedReturnValue = useMemo(
    () => ({
      flattenedSharedBoardsTitleMap,
      isLoading,
    }),
    [flattenedSharedBoardsTitleMap, isLoading],
  );

  return memoizedReturnValue;
};
